import React from "react";
import { calculateDuration } from "./calculateduration";
import { SERVICES } from "../configs/breakingNewsConfig";

interface BNPreviewProps {
  formData: {
    affectedVehicles: string[];
    affectedModules: string[];
    affectedBrands: string[];
    affectedMarkets: string[];
    affectedServices: string[];
    outageType: string;
    leadIncidentId: string;
    startDisturbance: string;
    endDisturbance: string;
    customerImpactText: string;
    technicalDescription: string;
    status: string;
    gmtOffset: string;
  };
}

// Define an interface for the detail items
interface DetailItem {
  label: string;
  value: string[] | string | React.ReactElement | null;
}

const basePath = ""; // for header/footer images

const BNPreview: React.FC<BNPreviewProps> = ({ formData }) => {
  const allowedBrands = ["All brands", "All Brands (except Ford)", "Seat&Cupra", "Volkswagen", "Skoda", "Ford"];

  // --- PDE Service Exception Logic ---
  const pdeBrands = ["Seat&Cupra", "All brands"];
  const pdeTriggerKeywords = [
    "Enrollment", "MSG", "IDK", "VUM", "GroupRetailPortal", "MessageHub"
  ];
  // Check if PDE rule applies
  const shouldAddPDE =
    formData.affectedBrands.some(brand => pdeBrands.includes(brand)) &&
    (
      formData.affectedModules.concat(formData.affectedServices)
        .some(item =>
          pdeTriggerKeywords.some(keyword =>
            item.toLowerCase().includes(keyword.toLowerCase())
          )
        )
    );

  // Compose affectedServices for preview (add PDE if needed, but don't mutate original)
  let previewAffectedServices = formData.affectedServices;
  if (shouldAddPDE && !previewAffectedServices.includes("PDE (PreDelivery Enrollment)")) {
    previewAffectedServices = [...previewAffectedServices, "PDE (PreDelivery Enrollment)"];
  }

  const parseDate = (dateString: string | undefined): string => {
    // Return empty string if dateString is undefined or empty
    if (!dateString) return "";
    try {
      const [day, month, yearTime] = dateString.split('.');
      if (!yearTime) return dateString; // Return original if not in expected format
      const [year, time] = yearTime.split(' ');
      if (!time) return dateString;
      const [hours, minutes] = time.split(':');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
      // In case of any parsing errors, return the original string
      return dateString;
    }
  };

  // Calculate duration if both start and end times exist
  const incidentDuration = formData.startDisturbance && formData.endDisturbance ?
    calculateDuration(formData.startDisturbance, formData.endDisturbance) : "";

  const getBaseStatus = (status: string) =>
    status.replace(/\s*-\s*Update$/i, "").trim();

  const getStatusColor = (status: string) => {
    switch (getBaseStatus(status)) {
      case "Troubleshooting":
        return "#ff4040";
      case "Fix Implementation":
        return "#ffffff";
      case "Resolved":
        return "#1eef97";
      case "Change":
        return "#d3d3d3";
      default:
        return "#ffffff";
    }
  };

  const renderStatus = (uppercase = false) => {
    const hasUpdateSuffix = /\s*-\s*Update$/i.test(formData.status);
    const baseStatusRaw = getBaseStatus(formData.status);
    const baseStatusText = uppercase ? baseStatusRaw.toUpperCase() : baseStatusRaw;
    const updateText = uppercase ? " - UPDATE" : " - Update";

    return (
      <>
        <span style={{ color: getStatusColor(baseStatusRaw) }}>{baseStatusText}</span>
        {hasUpdateSuffix && <span style={{ color: "#ffcc00" }}>{updateText}</span>}
      </>
    );
  };

  // Filter to only display allowed brands in the preview
  const filteredBrands = formData.affectedBrands.filter(brand =>
    allowedBrands.includes(brand)
  );

 // Determine interfaces and customer based on modules when SEAT / CUPRA is selected
  let interfacesAndCustomer: string | null = null;
  let seatCupraRule3Additions: string[] = [];

  // Only apply for Seat&Cupra brand (not All brands etc)
  const isSeatCupra = formData.affectedBrands.includes("Seat&Cupra");

  // Normalize helper for robust matching
  const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();

  if (isSeatCupra) {
    // Check both affected vehicles and modules arrays
    const allModuleValues = [...formData.affectedVehicles, ...formData.affectedModules].map(normalize);

    // Interfaces and Customer logic (robust to spaces/case)
    if (
      allModuleValues.some(mod => mod.includes("mod1")) &&
      (allModuleValues.some(mod => mod.includes("mod3.1")) || allModuleValues.some(mod => mod.includes("mod3.2")))
    ) {
      interfacesAndCustomer = "MY SEAT APP + CUPRA CONNECT";
    } else if (
      allModuleValues.some(mod => mod.includes("mod3.1")) || allModuleValues.some(mod => mod.includes("mod3.2"))
    ) {
      interfacesAndCustomer = "MY CUPRA APP + MY SEAT APP";
    } else if (allModuleValues.some(mod => mod.includes("mod1"))) {
      interfacesAndCustomer = "MY SEAT APP";
    } else if (allModuleValues.some(mod => mod.includes("mod4"))) {
      interfacesAndCustomer = "MY CUPRA APP";
    } else if (allModuleValues.some(mod => mod.includes("mod1-4"))) {
      interfacesAndCustomer = "MY SEAT APP + MY CUPRA APP";
    } else if (allModuleValues.some(mod => mod.includes("mod2.5"))) {
      interfacesAndCustomer = "MY CUPRA APP";
    } else {
      interfacesAndCustomer = "MY SEAT APP + MY CUPRA APP";
    }

    const hasProactiveEnrollment = previewAffectedServices.includes("Proactive - Enrollment");
    const hasMOD1 = formData.affectedVehicles.some(v => normalize(v) === "mod1");
    const hasMOD31 = formData.affectedVehicles.some(v => normalize(v) === "mod3.1");
    const hasMOD32 = formData.affectedVehicles.some(v => normalize(v) === "mod3.2");
    const hasMOD4 = formData.affectedVehicles.some(v => normalize(v) === "mod4");
    const hasMOD14 = formData.affectedVehicles.some(v => normalize(v) === "mod1-4");

    if (hasProactiveEnrollment) {
      if (hasMOD14) {
        seatCupraRule3Additions = ["Millage", "Enrollment", "QR-Enrollment"];
      } else if (hasMOD1 && (hasMOD31 || hasMOD32)) {
        seatCupraRule3Additions = ["Millage", "Enrollment"];
      } else if (hasMOD31 || hasMOD32) {
        seatCupraRule3Additions = ["Enrollment"];
      } else if (hasMOD1) {
        seatCupraRule3Additions = ["Millage"];
      } else if (hasMOD4) {
        seatCupraRule3Additions = ["QR-Enrollment"];
      }
    }
  }

  // Function to group services by their parent category
  const getGroupedServices = (): Array<{ parent: string, children: string[] }> => {
    // Early return if no services
    if (!previewAffectedServices || previewAffectedServices.length === 0) {
      return [];
    }

    // Create a map to group services by their parent categories
    const groupedServices: Map<string, string[]> = new Map();

    // Go through all service categories from config
    SERVICES.forEach(category => {
      // Find selected services that belong to this category
      const selectedChildren = category.children.filter(child =>
        previewAffectedServices.includes(child)
      );

      // Only add categories that have selected children
      if (selectedChildren.length > 0) {
        groupedServices.set(category.parent, selectedChildren);
      }
    });

    // If PDE was added but not found in any category, add it to "Others"
    if (
      previewAffectedServices.includes("PDE (PreDelivery Enrollment)") &&
      !Array.from(groupedServices.values()).flat().includes("PDE (PreDelivery Enrollment)")
    ) {
      // Find the "Others" category or create it
      let found = false;
      SERVICES.forEach(category => {
        if (
          category.parent.toLowerCase().includes("other") ||
          category.parent.toLowerCase().includes("misc")
        ) {
          const current = groupedServices.get(category.parent) || [];
          if (!current.includes("PDE (PreDelivery Enrollment)")) {
            groupedServices.set(category.parent, [...current, "PDE (PreDelivery Enrollment)"]);
          }
          found = true;
        }
      });
      // If no "Others" category exists, create one
      if (!found) {
        groupedServices.set("Others", ["PDE (PreDelivery Enrollment)"]);
      }
    }

    // Convert map to array of objects
    return Array.from(groupedServices.entries()).map(([parent, children]) => ({
      parent,
      children
    }));
  };

  // Get the grouped services
  const groupedServices = getGroupedServices();

  // Create event details array with proper typing
  const eventDetails: (DetailItem | null)[] = [
    { label: "Affected Vehicles", value: formData.affectedVehicles },
    { label: "Affected Modules", value: formData.affectedModules },
    { label: "Affected Brands", value: filteredBrands },
    { label: "Affected Markets", value: formData.affectedMarkets },
    groupedServices.length > 0 ? {
      label: "Affected Services",
      value: (
        <div style={{ margin: "5px 0" }}>
          {groupedServices.map((group, groupIndex) => (
            <div key={groupIndex} style={{ marginBottom: "8px" }}>
              <div style={{ fontWeight: "bold", paddingLeft: '10px' }}>• {group.parent}:</div>
              <div style={{ paddingLeft: "20px" }}>
                {group.children.map((service, serviceIndex) => (
                  <div key={serviceIndex}>- {service}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) as React.ReactElement
    } : null,
    { label: "Type of Outage", value: formData.outageType },
    {
      label: "Lead Incident ID",
      value: formData.leadIncidentId
        ? formData.leadIncidentId.toLowerCase().startsWith("ir")
          ? (
            <a
              href={`https://sc3.vwgroup.com/sc3/index.do?ctx=docEngine&file=probsummary&query=number%3D%22${formData.leadIncidentId}%22&action=&title=Incident%20${formData.leadIncidentId}`}
              style={{ color: "#ffffff", textDecoration: "underline" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {formData.leadIncidentId}
            </a>
          )
          : formData.leadIncidentId
        : "",
    },
    {
      label: `Start of Disturbance (Berlin Time ${formData.gmtOffset})`,
      value: parseDate(formData.startDisturbance),
    },
    {
      label: `End of Disturbance (Berlin Time ${formData.gmtOffset})`,
      value: parseDate(formData.endDisturbance),
    },
    // Add Incident Duration field if end time exists
    formData.endDisturbance ? {
      label: "Incident Duration (hh:mm)",
      value: incidentDuration,
    } : null,
  ];

  return (
    <div
      style={{
        maxWidth: "900px",
        maxHeight: "900px",
        overflow: "auto",
        margin: "auto",
        fontFamily: "Montserrat, sans-serif",
        backgroundColor: "#1d0638",
        padding: "20px",
        color: "white",
        borderRadius: "8px",
        border: "1px solid #ddd",
      }}
    >
      {/* Header with Images */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <img
          src={`${basePath}/header.png`}
          alt="Header 1"
          style={{ width: "100%", borderRadius: "5px", marginBottom: "10px" }}
        />
      </div>

      {/* Title */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <p
          style={{
            fontSize: "22px",
            fontWeight: "bold",
            color: "#ff0000",
            textTransform: "uppercase",
          }}
        >
          Breaking News
        </p>
        <p
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            textTransform: "uppercase",
            color: "#ffffff",
          }}
        >
          {renderStatus(true)}
        </p>
      </div>

      {/* Intro Text */}
      <div style={{ padding: "15px", marginBottom: "20px" }}>
        <p style={{ fontSize: "18px" }}>Dear Ladies and Gentlemen,</p>
        <p style={{ fontSize: "16px" }}>
          Please be informed about the following announcement affecting our
          services. Kindly review the details below.
        </p>
      </div>

      {/* Key Event Details */}
      <div
        style={{
          color: "#ffffff",
          padding: "15px",
          borderRadius: "5px",
        }}
      >
        {eventDetails
          .filter((item): item is DetailItem =>
            item !== null &&
            item.value !== null &&
            (Array.isArray(item.value)
              ? item.value.length > 0
              : typeof item.value === "string"
                ? item.value !== ""
                : true)
          )
          .map(({ label, value }) => (
            <div
              key={label}
              style={{
                marginBottom: "12px",
                padding: "10px",
                borderBottom: "1px solid #ddd",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              <strong>{label}: </strong>
              {Array.isArray(value) ? (
                <div style={{ margin: "5px 0" }}>
                  {value.map((item, index) => (
                    <div key={index} style={{ paddingLeft: '10px' }}>• {item}</div>
                  ))}
                </div>
              ) : (
                value
              )}
            </div>
          ))}

        {/* Customer Impact */}
        <div
          style={{
            marginBottom: "12px",
            padding: "10px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <strong>Customer Impact: </strong>
          <div style={{ paddingTop: "8px", wordWrap: "break-word" }}
            dangerouslySetInnerHTML={{
              __html: formData.customerImpactText
                ? formData.customerImpactText.replace(/\n/g, '<br />')
                : "No customer impact description"
            }}
          />
        </div>

        {/* Technical Description */}
        <div
          style={{
            marginBottom: "12px",
            padding: "10px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <strong>Technical Description: </strong>
          <div style={{ paddingTop: "8px", wordWrap: "break-word" }}
            dangerouslySetInnerHTML={{
              __html: formData.technicalDescription
                ? formData.technicalDescription.replace(/\n/g, '<br />')
                : "No technical description"
            }}
          />
        </div>
      </div>

      {/* Seat / Cupra Additional Information */}
      {interfacesAndCustomer && (
        <div style={{ padding: "15px", marginBottom: "20px" }}>
          <h3 style={{
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "10px",
            textAlign: "left"
          }}>
            Seat / Cupra Additional Information
          </h3>
          <div style={{
            padding: "10px",
            borderTop: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
            textAlign: "left",
          }}>
            <strong>Interfaces and Customer: </strong>{interfacesAndCustomer}
            {/* Show Rule 3 additions as a separate line, only for Seat&Cupra */}
            {seatCupraRule3Additions.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <strong>Affected Enrollment: </strong>
                {seatCupraRule3Additions.map((service, idx) => (
                  <span key={service}>
                    {service}{idx < seatCupraRule3Additions.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Apology Message */}
      <div
        style={{ padding: "15px", marginBottom: "10px", marginTop: "50px", textAlign: "center" }}
      >
        <p style={{ fontSize: "16px", margin: "0 0 10px 0" }}>
          We would like to apologize for any inconvenience caused.
        </p>
        <hr
          style={{ border: "0", borderTop: "1px solid #ddd", margin: "10px 0" }}
        />
      </div>

      {/* Additional Contact Section */}
      <div style={{ textAlign: "center", paddingLeft: "15px" }}>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {[
            { label: "Contact via email: ", linkText: "care-dsoc.mim@cariad.technology", link: "mailto:care-dsoc.mim@cariad.technology" },
            {
              label: "Click here to change your subscription to Announcements: ",
              linkText: "Subscription Self-Service",
              link: `https://announcement-tool.care.cariad.digital/subscription`
            },
            {
              label: "Click here to go to incident DICE dashboard: ",
              linkText: "Incident DICE Dashboard",
              link: `https://dice.cariad.digital/incidents`
            },
          ].map(({ label, linkText, link }, index) => (
            <li key={index} style={{ display: "flex", marginBottom: "10px" }}>
              <span style={{ color: "#ffffff", fontSize: "16px" }}>{label} </span>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#ffffff", textDecoration: "underline", fontSize: "16px", marginLeft: "5px" }}
              >
                {linkText}
              </a>
            </li>
          ))}
        </ul>

        {/* Website link with special spacing */}
        <div style={{ marginTop: "100px", paddingTop: "25px", borderTop: "1px solid #444", textAlign: "center" }}>
          <p style={{ color: "#ffffff", fontSize: "16px", marginBottom: "10px" }}>
            CARIAD Estonia - Digital services Operations center
          </p>
          <a
            href="https://cariad.technology/de/en/company/regions/estonia.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#ffffff", textDecoration: "underline", fontSize: "16px" }}
          >
            CARIAD Estonia
          </a>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", marginTop: "-20px" }}>
        <img
          src={`${basePath}/footer.png`}
          alt="Footer"
          style={{ width: "100%", borderRadius: "5px" }}
        />
      </div>
    </div>
  );
};

export default BNPreview;